// 約定結果 CSV (AC-BSP-115。 時間前は `bidding/intraday-bidding.md` AC-BIN-023 が同じ列を使う)。
//
// 出す行・約定電力量・約定金額・残数量は backend (`bidding.ExecutionLedgerOf`) が決めた値で、
// ここは列の並び・値の表示・セルのエスケープだけを担う。 数値は丸めずに受け取った文字列のまま出す
// (照合のとき「約定金額 = 約定価格 × 約定電力量 × 1000」 がずれないように)。
// 項目名・売/買・入札種別・ステータスは日本語の固定文言で出す。 エリアは画面と同じ `convertAreaLabel` (表示言語に従う)。
// 作成者は eauth member ID を呼び出し側の resolver で表示名に解決して出す。
// BOM 付与とダウンロードは `~/libs/downloadCsv` が担う。
import type { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'

import { convertAreaLabel } from '~/models/area'
import { type CivilDate, formatCivilDateSlash } from '~/models/civilDate'
import { komaNumberTimeRange } from '~/models/komaToTime'
import { csvCell, neutralizeFormula } from '~/pages/SpotBidding/models/csvCell'

export type ExecutionResultBidKind =
  | 'normal'
  | 'standard'
  | 'linkP'
  | 'linkC'
  | 'loopA'
  | 'loopB'

export type ExecutionResultStatus =
  | 'allMatched'
  | 'partialMatched'
  | 'unmatched'

/** CSV 1 行。 数値は backend が返した decimal 文字列。 */
export type ExecutionResultCsvLine = {
  deliveryDate: CivilDate
  koma: number
  areaCode: AreaCode
  deliveryContractCode?: string
  /** 時間前は undefined (空欄)。 */
  bidKind?: ExecutionResultBidKind
  side: Side
  bidPriceYenPerKwh: string
  bidVolumeMw: string
  /** 未約定は undefined (空欄)。 */
  matchedPriceYenPerKwh?: string
  matchedVolumeMw: string
  matchedEnergyMwh: string
  remainingVolumeMw: string
  /** 約定済みで約定価格が未取得の時は undefined (空欄)。 */
  matchedAmountYen: string | undefined
  status: ExecutionResultStatus
  jepxBidNo?: string
  /** スポットは常に undefined (JEPX が約定番号を出さない)。 */
  executionNo?: string
  createdBy?: string
  workflowId?: string
  memoJepx?: string
  memoFree?: string
}

const BID_KIND_LABEL: Record<ExecutionResultBidKind, string> = {
  normal: '通常',
  standard: 'STANDARD',
  linkP: 'LINK-P',
  linkC: 'LINK-C',
  loopA: 'LOOP-A',
  loopB: 'LOOP-B',
}

const STATUS_LABEL: Record<ExecutionResultStatus, string> = {
  allMatched: '全約定',
  partialMatched: '部分約定',
  unmatched: '未約定',
}

const SIDE_LABEL: Partial<Record<Side, string>> = {
  [Side.BUY]: '買',
  [Side.SELL]: '売',
}

// 自由入力・外部由来の文字列は数式として評価されないようにする。 数値列は再集計を壊さないため対象外。
const text = (value: string | undefined): string =>
  neutralizeFormula(value ?? '')

export type BuildExecutionResultCsvOptions = {
  /**
   * 作成者 (member ID) を表示名へ解決する関数。 画面の作成列と同じ `useMemberDisplayName`
   * の戻り値を渡す。 渡し忘れで作成者が全行空にならないよう必須にする。
   */
  resolveCreatedBy: (memberId: string | undefined) => string | undefined
}

const COLUMNS: ReadonlyArray<{
  header: string
  value: (
    line: ExecutionResultCsvLine,
    options: BuildExecutionResultCsvOptions,
  ) => string
}> = [
  { header: '受渡日', value: (l) => formatCivilDateSlash(l.deliveryDate) },
  { header: 'コマ', value: (l) => String(l.koma) },
  { header: '時間帯', value: (l) => komaNumberTimeRange(l.koma, '-') },
  { header: 'エリア', value: (l) => convertAreaLabel(l.areaCode) },
  { header: '受渡契約コード', value: (l) => text(l.deliveryContractCode) },
  {
    header: '入札種別',
    value: (l) => (l.bidKind !== undefined ? BID_KIND_LABEL[l.bidKind] : ''),
  },
  { header: '売/買', value: (l) => SIDE_LABEL[l.side] ?? '' },
  { header: '入札価格(円/kWh)', value: (l) => l.bidPriceYenPerKwh },
  { header: '入札数量(MW)', value: (l) => l.bidVolumeMw },
  { header: '約定価格(円/kWh)', value: (l) => l.matchedPriceYenPerKwh ?? '' },
  { header: '約定数量(MW)', value: (l) => l.matchedVolumeMw },
  { header: '約定電力量(MWh)', value: (l) => l.matchedEnergyMwh },
  { header: '残数量(MW)', value: (l) => l.remainingVolumeMw },
  { header: '約定金額(円)', value: (l) => l.matchedAmountYen ?? '' },
  { header: 'ステータス', value: (l) => STATUS_LABEL[l.status] },
  { header: '入札番号', value: (l) => text(l.jepxBidNo) },
  { header: '約定番号', value: (l) => text(l.executionNo) },
  {
    header: '作成者',
    // 作成者は eauth member ID なので生値を出さず、 入札一覧 CSV (`spotBidCsv`) と同じく
    // 表示名へ解決する (解決できない時は空)。
    value: (l, { resolveCreatedBy }) => text(resolveCreatedBy(l.createdBy)),
  },
  { header: 'ワークフローID', value: (l) => text(l.workflowId) },
  { header: 'JEPX 自由記述欄', value: (l) => text(l.memoJepx) },
  { header: 'メモ', value: (l) => text(l.memoFree) },
]

export const EXECUTION_RESULT_CSV_HEADERS: readonly string[] = COLUMNS.map(
  (c) => c.header,
)

/** ヘッダー行 + 1 行 1 札の CSV 本文 (BOM なし)。 */
export const buildExecutionResultCsv = (
  lines: readonly ExecutionResultCsvLine[],
  options: BuildExecutionResultCsvOptions,
): string =>
  [
    EXECUTION_RESULT_CSV_HEADERS.map(csvCell).join(','),
    ...lines.map((line) =>
      COLUMNS.map((c) => csvCell(c.value(line, options))).join(','),
    ),
  ].join('\n')

const yyyymmdd = ({ year, month, day }: CivilDate): string =>
  `${year}${String(month).padStart(2, '0')}${String(day).padStart(2, '0')}`

export const spotExecutionResultCsvFilename = (
  from: CivilDate,
  to: CivilDate,
): string => `スポット約定結果_${yyyymmdd(from)}-${yyyymmdd(to)}.csv`
